import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneIncoming,
  User,
  Clock,
  MessageSquare,
  AlertTriangle,
  Loader2,
  ArrowRightLeft,
  UserPlus,
} from 'lucide-react';
import { voiceService, VoiceSession, VoiceStats, HandoffState } from '@/services/voice.service';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { useAuthStore } from '@stores/authStore';
import { TransferDialog } from '@/features/voice/components/TransferDialog';
import { callControls } from '@/features/voice/callConference';
import { useWebSocket } from '@/hooks/useWebSocket';
import { formatDistanceToNow } from 'date-fns';

interface VoiceQueueProps {
  onSessionSelect?: (session: VoiceSession) => void;
}

/** The floating shell, same card as every other surface on the console. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

/** Session status implied by a handoff state; terminal states leave it to call-ended. */
const HANDOFF_STATUS: Partial<Record<HandoffState, VoiceSession['status']>> = {
  requested: 'handoff_pending',
  queued: 'handoff_pending',
  agent_reserved: 'handoff_pending',
  connecting: 'handoff_pending',
  connected: 'with_agent',
};

/** How long an ended call stays in the live view. */
export const ENDED_VISIBLE_MS = 10 * 60_000;

const FILTERS: ReadonlyArray<[QueueFilter, string]> = [
  ['all', 'All'],
  ['waiting', 'Waiting for agent'],
  ['ai', 'With AI'],
  ['agent', 'With agent'],
  ['ended', 'Ended'],
];

export type QueueFilter = 'all' | 'waiting' | 'ai' | 'agent' | 'ended';

/** Which filter chip a live call falls under. */
export function filterOf(session: VoiceSession): Exclude<QueueFilter, 'all' | 'ended'> {
  if (session.status === 'handoff_pending') return 'waiting';
  if (session.status === 'with_agent') return 'agent';
  return 'ai';
}

/**
 * A caller an agent can take: waiting in the queue, or ringing for a
 * colleague who has not answered. Not one that is ringing for this agent -
 * they answer that in the call window.
 */
export function isClaimable(session: VoiceSession, currentUserId?: string): boolean {
  if (session.status !== 'handoff_pending') return false;
  const state = session.handoff?.state;
  if (state === 'queued') return true;
  return state === 'connecting' && !!session.handoff?.reservedAgentId && session.handoff.reservedAgentId !== currentUserId;
}

/** Roles that see every live call, including those still with the AI. */
export function seesEveryCall(role: string | undefined): boolean {
  return ['owner', 'admin', 'supervisor'].includes(String(role || '').toLowerCase());
}

/** Who else is on a 3-way call, for its row. */
export function onCallLine(session: VoiceSession, names: Record<string, string>): string {
  const people = (session.handoff?.participants ?? []).map((p) => {
    const name = p.agentName || names[p.agentId] || 'A colleague';
    return p.state === 'ringing' ? `${name} (being added)` : name;
  });
  return `Also on the call: ${people.join(', ')}`;
}

/** Where a waiting caller is, in a line an agent reads at a glance. */
export function waitingLine(
  session: VoiceSession,
  currentUserId: string | undefined,
  names: Record<string, string>,
): string | null {
  const handoff = session.handoff;
  if (session.status !== 'handoff_pending' || !handoff) return null;
  if (handoff.state === 'queued') return 'Waiting in the queue for the next free agent';
  if (handoff.state === 'connecting') {
    if (handoff.reservedAgentId && handoff.reservedAgentId === currentUserId) {
      return 'Ringing you - answer it in the call window';
    }
    const who = handoff.reservedAgentId ? names[handoff.reservedAgentId] : undefined;
    return `Ringing ${who || 'an agent'} - you can answer it instead`;
  }
  return 'Finding an agent…';
}

/** One tone per session status, spoken in tokens rather than raw colour. */
const STATUS_TONE: Record<string, { wash: string; line: string; ink: string }> = {
  ringing: {
    wash: 'var(--co-terracotta-wash)',
    line: 'var(--co-terracotta-line)',
    ink: 'var(--co-terracotta-ink)',
  },
  ai_conversation: {
    wash: 'var(--co-machine-wash)',
    line: 'var(--co-machine-line)',
    ink: 'var(--co-machine-ink)',
  },
  handoff_pending: {
    wash: 'var(--co-terracotta-wash)',
    line: 'var(--co-terracotta-line)',
    ink: 'var(--co-terracotta-ink)',
  },
  with_agent: {
    wash: 'var(--co-green-wash)',
    line: 'var(--co-green-line)',
    ink: 'var(--co-green-ink)',
  },
};

export function VoiceQueue({ onSessionSelect }: VoiceQueueProps) {
  const [sessions, setSessions] = useState<VoiceSession[]>([]);
  // Calls that ended in the last few minutes: kept on screen, greyed, so a
  // call does not simply vanish the moment the caller hangs up.
  const [ended, setEnded] = useState<VoiceSession[]>([]);
  const [filter, setFilter] = useState<QueueFilter>('all');
  const [stats, setStats] = useState<VoiceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [claimErrors, setClaimErrors] = useState<Record<string, string>>({});
  /** Agent names by id, to say who a caller is ringing. */
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});

  const { subscribe, unsubscribe, on, off } = useWebSocket();
  const isOnline = useVoiceAgentStore((s) => s.isOnline);
  const isDeviceReady = useVoiceAgentStore((s) => s.isDeviceReady);
  const onACall = useVoiceAgentStore((s) => !!s.activeCall);
  const takeCall = useVoiceAgentStore((s) => s.takeCall);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.role);
  const canTakeCalls = isOnline && isDeviceReady && !onACall;
  // An agent sees calls once a person is wanted, and their own; admins see
  // all. The server enforces it; these keep the live updates in step.
  const seesAllCalls = seesEveryCall(role);
  const viewerRef = useRef({ seesAllCalls, currentUserId });
  viewerRef.current = { seesAllCalls, currentUserId };
  // This agent's own call, for its Transfer and Merge buttons on its row.
  const activeCall = useVoiceAgentStore((s) => s.activeCall);
  const callContext = useVoiceAgentStore((s) => s.callContext);
  const callParticipants = useVoiceAgentStore((s) => s.participants);
  const isGuest = useVoiceAgentStore((s) => s.isGuest);
  const myCall = callControls({ activeCall, callContext, participants: callParticipants, isGuest });
  const [callDialog, setCallDialog] = useState<'transfer' | 'conference' | null>(null);
  /** Why Accept cannot be pressed right now, in the agent's terms. */
  const cannotTakeBecause = !isOnline || !isDeviceReady
    ? 'Go online to take calls'
    : onACall
      ? 'Finish your current call first'
      : null;

  /**
   * Take a caller - waiting, or ringing for a colleague - and answer it as
   * soon as it reaches this agent. Of two agents pressing Accept together,
   * the server gives the call to one; the other is told who was quicker.
   */
  const claimingRef = useRef(false);
  const claimCall = useCallback(async (sessionId: string) => {
    // A ref as well as state: two quick clicks land before a re-render.
    if (claimingRef.current) return;
    claimingRef.current = true;
    setClaiming(sessionId);
    setClaimErrors((prev) => {
      const { [sessionId]: _dropped, ...rest } = prev;
      return rest;
    });
    try {
      await takeCall(sessionId);
      // The call reaches this agent's phone and is answered there; the row
      // updates from voice:handoff:updated.
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Could not take this call. Try again.';
      setClaimErrors((prev) => ({ ...prev, [sessionId]: message }));
    } finally {
      claimingRef.current = false;
      setClaiming(null);
    }
  }, [takeCall]);

  // Fetch active sessions
  const fetchSessions = useCallback(async () => {
    try {
      const [sessionsData, statsData, endedData, roster] = await Promise.all([
        voiceService.getActiveSessions(),
        voiceService.getSessionStats(),
        // Optional: the live list stands on its own if these fail.
        voiceService.getRecentlyEnded().catch(() => null),
        // Inside .then so even a missing method cannot take the queue down.
        Promise.resolve().then(() => voiceService.getAgentRoster()).catch(() => null),
      ]);
      setSessions(sessionsData);
      setStats(statsData);
      if (endedData) setEnded(endedData);
      if (roster) {
        setAgentNames(
          Object.fromEntries(roster.filter((a) => a.agentName).map((a) => [a.agentId, a.agentName as string])),
        );
      }
      setError(null);
    } catch (err) {
      setError('Failed to load voice sessions');
      console.error('Error fetching voice sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle WebSocket events
  useEffect(() => {
    // Subscribe to voice queue
    subscribe('subscribe_voice_queue', {});

    // Handle voice events with null safety
    const handleCallStarted = (data: any) => {
      if (!data?.sessionId) {
        console.warn('[VoiceQueue] Received call started event without sessionId');
        return;
      }
      // A call the AI is handling is not shown to agents.
      if (!viewerRef.current.seesAllCalls) return;
      setSessions((prev) => {
        // Add new session if not exists
        const exists = prev.find((s) => s.sessionId === data.sessionId);
        if (exists) return prev;
        return [
          {
            sessionId: data.sessionId,
            callSid: data.callSid || '',
            from: data.from || 'Unknown',
            to: data.to || '',
            status: 'ai_conversation',
            messageCount: 0,
            createdAt: data.startedAt || new Date().toISOString(),
            callerCity: data.callerCity,
            callerCountry: data.callerCountry,
          },
          ...prev,
        ];
      });
      setStats((prev) => prev ? { ...prev, active: prev.active + 1, aiHandled: prev.aiHandled + 1 } : null);
    };

    const handleHandoffRequested = (data: any) => {
      if (!data?.sessionId) {
        console.warn('[VoiceQueue] Received handoff event without sessionId');
        return;
      }
      // A caller now waiting for a person: an agent who could not see the
      // call before gets it from the server, as much as they may see.
      if (!viewerRef.current.seesAllCalls) {
        void fetchSessions();
        return;
      }
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === data.sessionId
            ? { ...s, status: 'handoff_pending', handoffReason: data.reason }
            : s
        )
      );
      setStats((prev) =>
        prev ? { ...prev, aiHandled: Math.max(0, prev.aiHandled - 1), pendingHandoff: prev.pendingHandoff + 1 } : null
      );
    };

    const handleCallWithAgent = (data: any) => {
      if (!data?.sessionId) {
        console.warn('[VoiceQueue] Received call with agent event without sessionId');
        return;
      }
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === data.sessionId ? { ...s, status: 'with_agent' } : s
        )
      );
      setStats((prev) =>
        prev ? { ...prev, pendingHandoff: Math.max(0, prev.pendingHandoff - 1), withAgent: prev.withAgent + 1 } : null
      );
    };

    const handleCallEnded = (data: any) => {
      if (!data?.sessionId) {
        console.warn('[VoiceQueue] Received call ended event without sessionId');
        return;
      }
      let finished: VoiceSession | undefined;
      setSessions((prev) => {
        finished = prev.find((s) => s.sessionId === data.sessionId);
        return prev.filter((s) => s.sessionId !== data.sessionId);
      });
      setEnded((prev) => {
        if (!finished || prev.some((s) => s.sessionId === data.sessionId)) return prev;
        return [{ ...finished, status: 'completed', updatedAt: new Date().toISOString() }, ...prev];
      });
      setStats((prev) => (prev ? { ...prev, active: Math.max(0, prev.active - 1) } : null));
    };

    const handleHandoffUpdated = (data: any) => {
      if (!data?.sessionId || !data?.state) return;
      const { seesAllCalls: all, currentUserId: me } = viewerRef.current;
      if (!all) {
        const onIt =
          data.agentId === me || (Array.isArray(data.participants) && data.participants.some((p: any) => p?.agentId === me));
        // A colleague has the call now: it is theirs, not this agent's to see.
        if (data.state === 'connected' && !onIt) {
          setSessions((prev) => prev.filter((s) => s.sessionId !== data.sessionId));
          return;
        }
        // Reaching this agent (or newly waiting): fetch what they may now see.
        if (onIt || data.state === 'queued' || data.state === 'connecting') void fetchSessions();
      }
      // Whatever went wrong taking this call is out of date now.
      setClaimErrors((prev) => {
        if (!(data.sessionId in prev) || data.state === 'connected') return prev;
        const { [data.sessionId]: _dropped, ...rest } = prev;
        return rest;
      });
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === data.sessionId
            ? {
                ...s,
                status: HANDOFF_STATUS[data.state as HandoffState] ?? s.status,
                handoff: {
                  ...(s.handoff ?? { requestedAt: s.createdAt }),
                  state: data.state,
                  attemptId: data.attemptId ?? s.handoff?.attemptId ?? '',
                  reservedAgentId: data.agentId,
                  queuedAt: data.queuedAt ?? s.handoff?.queuedAt,
                  participants: Array.isArray(data.participants) ? data.participants : s.handoff?.participants,
                },
              }
            : s
        )
      );
    };

    on('voice:call:started', handleCallStarted);
    on('voice:handoff:updated', handleHandoffUpdated);
    on('voice:handoff:requested', handleHandoffRequested);
    on('voice:call:with_agent', handleCallWithAgent);
    on('voice:call:ended', handleCallEnded);

    // Initial fetch
    fetchSessions();

    // Cleanup
    return () => {
      unsubscribe('unsubscribe_voice_queue', {});
      off('voice:call:started', handleCallStarted);
      off('voice:handoff:updated', handleHandoffUpdated);
      off('voice:handoff:requested', handleHandoffRequested);
      off('voice:call:with_agent', handleCallWithAgent);
      off('voice:call:ended', handleCallEnded);
    };
  }, [subscribe, unsubscribe, on, off, fetchSessions]);

  // Refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);


  /** The status glyph, coloured from the palette the rest of the product uses. */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ringing':
        return <Phone className="h-4 w-4 animate-pulse" style={{ color: 'var(--co-terracotta)' }} />;
      case 'ai_conversation':
        return <MessageSquare className="h-4 w-4" style={{ color: 'var(--co-machine-ink)' }} />;
      case 'handoff_pending':
        return (
          <AlertTriangle className="h-4 w-4 animate-pulse" style={{ color: 'var(--co-terracotta)' }} />
        );
      case 'with_agent':
        return <User className="h-4 w-4" style={{ color: 'var(--co-green-ink)' }} />;
      case 'completed':
        return <PhoneOff className="h-4 w-4" style={{ color: 'var(--co-ink-3)' }} />;
      default:
        return <PhoneCall className="h-4 w-4" style={{ color: 'var(--co-ink-3)' }} />;
    }
  };

  /** The status pill, same tones as the glyph. */
  const statusStyle = (status: string) => {
    const tone = STATUS_TONE[status] || {
      wash: 'var(--co-sunken)',
      line: 'var(--co-line)',
      ink: 'var(--co-ink-3)',
    };
    return { background: tone.wash, borderColor: tone.line, color: tone.ink };
  };

  if (loading) {
    return (
      <div
        className={`${CARD_CLASS} flex-none p-8 text-center text-[12.5px] text-[var(--co-ink-3)]`}
        style={CARD}
      >
        Loading voice queue...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex-none p-8 text-center text-[12.5px]"
        style={{
          ...CARD,
          border: '1px solid var(--co-burgundy-line)',
          background: 'var(--co-burgundy-wash)',
          color: 'var(--co-burgundy)',
        }}
      >
        {error}
        <button
          onClick={fetchSessions}
          className="ml-2 font-semibold text-[var(--co-brass-ink)] hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const now = Date.now();
  const live = sessions.filter((s) => !ended.some((e) => e.sessionId === s.sessionId));
  const recentlyEnded = ended.filter(
    (s) => now - Date.parse(s.updatedAt || s.createdAt) < ENDED_VISIBLE_MS,
  );
  const visible = filter === 'all' ? live : live.filter((s) => filterOf(s) === filter);
  const counts: Record<QueueFilter, number> = {
    all: live.length,
    waiting: live.filter((s) => filterOf(s) === 'waiting').length,
    ai: live.filter((s) => filterOf(s) === 'ai').length,
    agent: live.filter((s) => filterOf(s) === 'agent').length,
    ended: recentlyEnded.length,
  };

  const renderRow = (session: VoiceSession, isEnded: boolean) => (
      <div
        key={session.sessionId}
        className={`cursor-pointer px-5 py-4 transition-colors hover:bg-[var(--co-selected)] ${isEnded ? 'opacity-60' : ''}`}
        style={
          !isEnded && session.status === 'handoff_pending'
            ? { background: 'var(--co-terracotta-wash)' }
            : undefined
        }
        onClick={() => onSessionSelect?.(session)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-[2px] flex-none">{getStatusIcon(isEnded ? 'completed' : session.status)}</span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="co-mono text-[13px] font-semibold text-[var(--co-ink)]">
                  {voiceService.formatPhoneNumber(session.from)}
                </span>
                <span
                  className="co-mono rounded-full border px-2 py-[2px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
                  style={statusStyle(isEnded ? 'completed' : session.status)}
                >
                  {isEnded ? 'Ended' : voiceService.getStatusLabel(session.status)}
                </span>
              </div>
              <div className="mt-[5px] flex flex-wrap items-center gap-3 text-[11.5px] text-[var(--co-ink-3)]">
                {session.callerCity && session.callerCountry && (
                  <span>
                    {session.callerCity}, {session.callerCountry}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {isEnded
                    ? `ended ${formatDistanceToNow(new Date(session.updatedAt || session.createdAt), { addSuffix: true })}`
                    : formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {session.messageCount} messages
                </span>
              </div>
              {session.lastTranscript && (
                <p className="mt-[6px] max-w-[70ch] truncate text-[12px] text-[var(--co-ink-2)]">
                  "{session.lastTranscript}"
                </p>
              )}
              {!isEnded && session.handoffReason && session.status === 'handoff_pending' && (
                <p className="mt-[6px] text-[12px]" style={{ color: 'var(--co-terracotta-ink)' }}>
                  Handoff reason: {session.handoffReason}
                </p>
              )}
              {!isEnded && waitingLine(session, currentUserId, agentNames) && (
                <p
                  className="mt-[6px] flex items-center gap-[6px] text-[12px] font-medium"
                  style={{ color: 'var(--co-ink-2)' }}
                >
                  <PhoneIncoming className="h-[13px] w-[13px] flex-none" style={{ color: 'var(--co-terracotta)' }} />
                  {waitingLine(session, currentUserId, agentNames)}
                </p>
              )}
              {!isEnded && !!session.handoff?.participants?.length && (
                <p className="mt-[6px] flex items-center gap-[6px] text-[12px] font-medium" style={{ color: 'var(--co-ink-2)' }}>
                  <UserPlus className="h-[13px] w-[13px] flex-none" aria-hidden />
                  {onCallLine(session, agentNames)}
                </p>
              )}
              {claimErrors[session.sessionId] && (
                <p className="mt-[6px] text-[12px]" role="alert" style={{ color: 'var(--co-burgundy)' }}>
                  {claimErrors[session.sessionId]}
                </p>
              )}
            </div>
          </div>
          {!isEnded && isClaimable(session, currentUserId) && (
            <div className="flex flex-none flex-col items-end gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void claimCall(session.sessionId);
                }}
                disabled={!canTakeCalls || claiming !== null}
                aria-busy={claiming === session.sessionId}
                title={cannotTakeBecause ?? 'Answer this caller now'}
                className="flex h-[38px] items-center gap-2 rounded-lg px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: 'var(--co-green)' }}
              >
                {claiming === session.sessionId ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Phone className="h-4 w-4" aria-hidden />
                )}
                {claiming === session.sessionId ? 'Connecting…' : 'Accept Call'}
              </button>
              {cannotTakeBecause && (
                <span className="text-[11px] text-[var(--co-ink-3)]">{cannotTakeBecause}</span>
              )}
            </div>
          )}
          {!isEnded && callContext?.sessionId === session.sessionId && (myCall.canTransfer || myCall.canAddColleague) && (
            <div className="flex flex-none items-center gap-2">
              {myCall.canTransfer && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCallDialog('transfer');
                  }}
                  title="Transfer your call to a colleague"
                  className="flex h-[34px] items-center gap-[6px] rounded-lg border border-[var(--co-line-strong)] px-3 text-[12.5px] font-semibold text-[var(--co-ink-2)] hover:bg-[var(--co-selected)]"
                >
                  <ArrowRightLeft className="h-[14px] w-[14px]" aria-hidden />
                  Transfer
                </button>
              )}
              {myCall.canAddColleague && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCallDialog('conference');
                  }}
                  title="Add a colleague to your call"
                  className="flex h-[34px] items-center gap-[6px] rounded-lg border border-[var(--co-line-strong)] px-3 text-[12.5px] font-semibold text-[var(--co-ink-2)] hover:bg-[var(--co-selected)]"
                >
                  <UserPlus className="h-[14px] w-[14px]" aria-hidden />
                  Merge
                </button>
              )}
            </div>
          )}
        </div>
      </div>
  );

  return (
    <div className={`${CARD_CLASS} flex-none overflow-hidden`} style={CARD}>
      {/* Header with Stats */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--co-line)] px-5 py-[14px]">
        <h2 className="flex items-center gap-2 text-[14px] font-bold text-[var(--co-ink)]">
          <PhoneCall className="h-[17px] w-[17px] text-[var(--co-ink-3)]" />
          Voice Queue
        </h2>
        {stats && (
          <div className="co-mono flex flex-wrap items-center gap-4 text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
            <span className="flex items-center gap-[6px]">
              <span className="h-2 w-2 rounded-full" style={{ background: 'var(--co-machine-ink)' }} />
              AI: {stats.aiHandled}
            </span>
            <span className="flex items-center gap-[6px]">
              <span
                className="h-2 w-2 animate-pulse rounded-full"
                style={{ background: 'var(--co-terracotta)' }}
              />
              Pending: {stats.pendingHandoff}
            </span>
            <span className="flex items-center gap-[6px]">
              <span className="h-2 w-2 rounded-full" style={{ background: 'var(--co-green)' }} />
              With Agent: {stats.withAgent}
            </span>
          </div>
        )}
      </div>

      {/* Filters: what the queue shows. Counts are what is on screen. */}
      <div className="flex flex-wrap gap-[6px] border-b border-[var(--co-line-subtle)] px-5 py-[10px]" role="tablist" aria-label="Filter calls">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            onClick={() => setFilter(key)}
            className="co-mono rounded-full border px-[10px] py-[3px] text-[10px] font-semibold uppercase tracking-[.1em]"
            style={
              filter === key
                ? { background: 'var(--co-ink)', borderColor: 'var(--co-ink)', color: 'var(--co-on-ink)' }
                : { background: 'var(--co-raised)', borderColor: 'var(--co-line)', color: 'var(--co-ink-3)' }
            }
          >
            {label} · {counts[key]}
          </button>
        ))}
      </div>

      {/* Session List */}
      <div className="divide-y divide-[var(--co-line-subtle)]">
        {filter !== 'ended' &&
          (visible.length === 0 ? (
            <div className="p-10 text-center text-[12.5px] text-[var(--co-ink-3)]">
              <Phone className="mx-auto mb-3 h-10 w-10 text-[var(--co-line-strong)]" />
              <p>{filter === 'all' ? 'No active voice calls' : 'No calls like this right now'}</p>
            </div>
          ) : (
            visible.map((session) => renderRow(session, false))
          ))}
        {(filter === 'all' || filter === 'ended') && recentlyEnded.length > 0 && (
          <>
            <p className="co-mono bg-[var(--co-sunken)] px-5 py-[6px] text-[9.5px] font-semibold uppercase tracking-[.14em] text-[var(--co-ink-3)]">
              Recently ended
            </p>
            {recentlyEnded.map((session) => renderRow(session, true))}
          </>
        )}
        {filter === 'ended' && recentlyEnded.length === 0 && (
          <p className="p-10 text-center text-[12.5px] text-[var(--co-ink-3)]">No calls ended in the last 10 minutes</p>
        )}
      </div>
      {callDialog && <TransferDialog mode={callDialog} onClose={() => setCallDialog(null)} />}
    </div>
  );
}

export default VoiceQueue;
